import React, { useEffect } from "react";
import {
  StyledIconButton,
  StyledToggleButton,
  PrimaryButton,
  SecondaryButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterList from "@mui/icons-material/FilterList";
import { useState } from "react";
import {
  StyledDialog,
  StyledDialogTitle,
} from "../../../components/StyledComponents/Dialog/Dialog.js";
import {
  H5HeadlineMedium,
  H6HeadlineSmall,
  H8TitleMedium,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  DialogContent,
  FormControl,
  InputAdornment,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import { StyledTextField, StyledFormControl } from "../../../components/StyledComponents/Inputs/Inputs.js";

import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { usePostAlertConfig } from "../../queries/usePostAlertConfig.ts";
import { useQuery } from "@tanstack/react-query";
import { env } from "../../../env.js";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

export const AddNotificationConfigurationDialog = (
  props
) => {
  const {
    refetch,
    openNotificationConfigurationDialog,
    setOpenNotificationConfigurationDialog,
    authToken,
  } = props;
  const { colors } = useTheme();
  const { data, isFetched: isAlertConfigFetched } =
    useQuery({
      queryKey: [
        "getAlertNotificationConfigAtUser",
        authToken,
      ],
      queryFn: async () => {
        try {
          const response = await fetch(
            `${env.APIPath.setAlertConfig}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
            }
          );
          return response.json();
        } catch (error) {
          console.error("Error fetching data:", error);
          throw error;
        }
      },
      enabled:
        openNotificationConfigurationDialog && !!authToken,
    });
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { mutate: addNotificationConfiguration } =
    usePostAlertConfig();

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      numberOfChipsOverThreshold: 30,
      chipTempThreshold: 95,
      desiredAlertInterval: 30,
    },
  });

  useEffect(() => {
    if (
      isAlertConfigFetched &&
      data.alertNotificationConfig
    ) {
      const {
        numberOfChipsOverThreshold,
        chipTempThreshold,
        desiredAlertInterval,
      } = data.alertNotificationConfig;

      reset({
        numberOfChipsOverThreshold:
          parseInt(numberOfChipsOverThreshold) || 30,
        chipTempThreshold:
          parseInt(chipTempThreshold) || 95,
        desiredAlertInterval:
          parseInt(desiredAlertInterval) || 0,
      });
    }
  }, [isAlertConfigFetched, data, setValue]);

  const onSubmit = (data) => {
    const finalData = {
      numberOfChipsOverThreshold: !isNaN(
        parseInt(data.numberOfChipsOverThreshold)
      )
        ? parseInt(data.numberOfChipsOverThreshold)
        : 0,
      chipTempThreshold: !isNaN(
        parseInt(data.chipTempThreshold)
      )
        ? parseInt(data.chipTempThreshold)
        : 0,

      desiredAlertInterval: !isNaN(
        parseInt(data.desiredAlertInterval)
      )
        ? parseInt(data.desiredAlertInterval)
        : 0,
    };
    addNotificationConfiguration(finalData, {
      onSuccess: async (response) => {
        if (response.status === "ok") {
          setToastMessage(
            "Notification configuration added successfully"
          );
          setToastOpen(true);
          await refetch();
          setOpenNotificationConfigurationDialog(false);
          reset();
        } else {
          setToastMessage(
            "Error while adding notification configuration"
          );
          setToastOpen(true);
        }
      },
      onError: (error) => {
        setToastMessage(
          "Error while adding notification configuration"
        );
        setToastOpen(true);
      },
    });
  };

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"lg"}
      open={openNotificationConfigurationDialog}
      hideBackdrop
    >
      <StyledDialogTitle sx={{ paddingTop: 0 }}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <H5HeadlineMedium style={{ textAlign: "left" }}>
            Add Notification configuration
          </H5HeadlineMedium>
          <StyledIconButton
            colors={colors}
            onClick={() => {
              setOpenNotificationConfigurationDialog(false);
              reset();
            }}
            style={{ textAlign: "right" }}
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={8}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="desiredAlertInterval-container"
            >
              <H8TitleMedium>
                Desired alert interval
              </H8TitleMedium>
              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="desiredAlertInterval"
                    control={control}
                    rules={{
                      required:
                        "desiredAlertInterval is required",
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        {...field}
                        {...register(
                          "desiredAlertInterval"
                        )}
                        variant="outlined"
                        label="Desired Alert Interval in mins"
                        fullWidth
                        type="number"
                        {...register(
                          "desiredAlertInterval",
                          {
                            required:
                              "This field is required",
                            min: {
                              value: 30,
                              message:
                                "Desired alert interval must be at least 30 minutes",
                            },
                          }
                        )}
                        error={errors?.desiredAlertInterval}
                        onChange={(event) =>
                          field.onChange(event.target.value)
                        }
                        helperText={
                          errors.desiredAlertInterval
                            ?.message
                        }
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>
            </Box>

            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="numberOfChipsOverThreshold-container"
            >
              <H8TitleMedium>
                Number of chips over threshold
              </H8TitleMedium>
              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="numberOfChipsOverThreshold"
                    control={control}
                    rules={{
                      required:
                        "number Of ChipsOverThreshold is required",
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        {...field}
                        {...register(
                          "numberOfChipsOverThreshold"
                        )}
                        variant="outlined"
                        label="Number of chips over threshold"
                        fullWidth
                        type="number"
                        {...register(
                          "numberOfChipsOverThreshold",
                          {
                            required:
                              "This field is required",
                            min: {
                              value: 20,
                              message:
                                "Number of chips over threshold must be at least 20",
                            },
                          }
                        )}
                        error={
                          errors.numberOfChipsOverThreshold
                        }
                        onChange={(event) =>
                          field.onChange(event.target.value)
                        }
                        helperText={
                          errors.numberOfChipsOverThreshold
                            ?.message
                        }
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="chipTempThreshold-container"
            >
              <H8TitleMedium>
                Chip temperature threshold
              </H8TitleMedium>
              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="chipTempThreshold"
                    control={control}
                    rules={{
                      required:
                        "chipTempThreshold is required",
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        {...field}
                        {...register("chipTempThreshold")}
                        variant="outlined"
                        label="Chip Temp Threshold in °C"
                        fullWidth
                        type="number"
                        {...register("chipTempThreshold", {
                          required:
                            "This field is required",
                          min: {
                            value: 95,
                            message:
                              "Chip temp threshold must be at least 95",
                          },
                        })}
                        error={errors.chipTempThreshold}
                        onChange={(event) =>
                          field.onChange(event.target.value)
                        }
                        helperText={
                          errors.chipTempThreshold?.message
                        }
                        inputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              °C
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>
            </Box>

            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              gap={4}
              className="action-button-container"
            >
              <PrimaryButton colors={colors} type="submit">
                Add
              </PrimaryButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};
